import { useCallback } from 'react';

import { type SegmentGlobalProps, useAnalyticsUtilities } from '@air/analytics';

export type PluginGlobalEventProps = Pick<
  SegmentGlobalProps,
  'accountType' | 'airAccountId' | 'app_name' | 'userEmail' | 'workspaceId' | 'workspaceName'
> & {
  /** The workspace ID from the plugin configuration URL params */
  configured_workspace_id?: string;
  /** The origin URL of the host application (e.g., Sanity Studio or Figma) */
  host_origin?: string;
  /** A unique identifier for the host app installation (e.g., Sanity project ID, Figma file ID) */
  unique_installation_id?: string;
};

/** Standard SegmentGlobalProps keys that map directly (not via extra) */
const STANDARD_KEYS: ReadonlySet<string> = new Set<keyof PluginGlobalEventProps>([
  'accountType',
  'airAccountId',
  'app_name',
  'userEmail',
  'workspaceId',
  'workspaceName',
]);

/**
 * Typed wrapper around `useAnalyticsUtilities` for plugin-specific analytics.
 *
 * Accepts a flat typed object and internally splits it into standard
 * `SegmentGlobalProps` fields vs `extra` for the analytics package.
 */
export const usePluginAnalytics = () => {
  const { setGlobalEventProps: _setGlobalEventProps, ...rest } = useAnalyticsUtilities();

  const setGlobalEventProps = useCallback(
    (params: PluginGlobalEventProps) => {
      const standard: Record<string, unknown> = {};
      const extra: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(params)) {
        if (STANDARD_KEYS.has(key)) {
          standard[key] = value;
        } else {
          extra[key] = value;
        }
      }

      _setGlobalEventProps({ ...standard, extra });
    },
    [_setGlobalEventProps],
  );

  return { ...rest, setGlobalEventProps };
};
